// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonIcon } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import StyledOverlay, { StyledOverlayComponent } from "@plumberssupply/App/Components/Common/StyledOverlay";
import { useClickAway } from "@plumberssupply/App/Hooks/useClickAway";
import Clickable from "@plumberssupply/MobiusOverrides/Clickable/Clickable";
import { color, xScreen } from "@plumberssupply/XDL/Utilities";
import React, { useState } from "react";
import { css, ThemeProps, withTheme } from "styled-components";

export interface FeedbackStyles {
    container?: InjectableCss;
    feedbackLabel?: TypographyProps;
    modal?: InjectableCss;
}

export const feedbackStyles: FeedbackStyles = {
    container: {
        css: css`
            background-color: ${color("primary")};
            position: sticky;
            top: 50%;
            left: 100%;
            padding: 9px 12px;
            border-radius: 4px 4px 0 0;
            cursor: pointer;
            border: 2px solid ${color("common.ui10")};
            transform: translate(30px, -50%) rotate(-90deg);
            z-index: ${getProp("theme.zIndex.feedback")};
            max-width: 100px;
            ${xScreen("lte", "sm")} {
                padding: 2px 4px;
            }
            ${xScreen("lte", "xs")} {
                display: none;
            }
        `,
    },
    feedbackLabel: {
        size: 16,
        weight: 400,
        color: "common.white",
        ellipsis: true,
        css: css`
            line-height: 16px;
            letter-spacing: 0;
            max-width: 72px;
            font-family: Arial;
            ${xScreen("lte", "sm")} {
                line-height: 20px;
                font-size: 18px;
                max-width: 80px;
            }
        `,
    },
    modal: {
        css: css`
            max-width: 570px;
            position: fixed;
            top: 20px;
            width: 90%;
            left: 0;
            right: 0;
            margin: 0 auto;
            padding: 20px;
            background: ${color("common.white")};
            &.fade-in {
                z-index: ${getProp("theme.zIndex.feedbackModal")};
                opacity: 1;
                transition: z-index 0s linear, opacity 0.5s linear;
                & + ${StyledOverlayComponent} {
                    z-index: ${getProp("theme.zIndex.modal")};
                }
            }
            &.fade-out {
                opacity: 0;
                z-index: -99;
                transition: opacity 0.5s linear, z-index 0s linear 0.5s;
            }

            & .iframe-container {
                position: relative;
                border: 1px solid ${color("common.ui6")};
                box-shadow: 0 0 10px ${color("common.ui7", 0.4)};
                background-color: ${color("common.white")};
            }
        `,
    },
};

const styles = feedbackStyles;

const Feedback = (props: ThemeProps<BaseTheme>) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const ref = useClickAway(() => {
        setIsModalOpen(false);
    });

    return (
        <>
            <Clickable {...styles.container} onClick={() => setIsModalOpen(true)} aria-label="send feedback">
                <Typography {...styles.feedbackLabel}>{translate("Feedback")}</Typography>
            </Clickable>
            <StyledWrapper {...styles.modal} ref={ref} className={isModalOpen ? "fade-in" : "fade-out"}>
                <Button
                    id="CloseModalButton"
                    onClick={() => setIsModalOpen(false)}
                    aria-labelledby="close-modal"
                    buttonType="outline"
                    shape="pill"
                    size={36}
                    {...props.theme.modal.defaultProps?.closeButtonProps}
                    data-test-selector="modalCloseButton"
                >
                    <ButtonIcon {...props.theme.modal.defaultProps?.closeButtonIconProps} />
                    <VisuallyHidden id="close-modal">{translate("Close modal")}</VisuallyHidden>
                </Button>
                <StyledWrapper className="iframe-container">
                    <iframe
                        className="airtable-embed"
                        src="https://airtable.com/embed/shrzPpRQJd94pDndc?backgroundColor=blue"
                        frameBorder="0"
                        width="100%"
                        height="533"
                    ></iframe>
                </StyledWrapper>
            </StyledWrapper>
            <StyledOverlay isOpen={isModalOpen} />
        </>
    );
};

export default withTheme(Feedback);
